import { useNumberScrub } from './useNumberScrub';
import { Children, forwardRef, useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type CSSProperties, type ReactNode } from 'react';
import { Icon, ICON_SIZE, type IconName } from '../editor/chrome/icons';
import { tip } from './tooltip';
import { beginHold } from './hold';
import { readNumber } from './readNumber';
import { Menu } from './menu';
import { useAnchor } from './popover';

/**
 * The controls, macOS 27's, rebuilt for a canvas that also has to work under a finger.
 *
 * Capsule push buttons, capsule segmented controls with a thumb that slides to the
 * choice, rounded fields with San Francisco's tabular figures, switches, pop-up buttons
 * with the up-and-down chevrons, colour wells. Metrics are the platform's regular size,
 * a notch taller where a toolbar mark has to be hit on a tablet.
 */

// ── toolbar ──────────────────────────────────────────────────────────────────

/**
 * A glass capsule holding a run of toolbar items. The toolbar is made of three of these
 * — leading, centre, trailing — as the HIG groups them; the capsule is the container, so
 * the items inside carry no borders of their own.
 */
export function ToolbarGroup({ children, className = '', label }: { children: ReactNode; className?: string; label?: string }) {
  return (
    <div role="group" aria-label={label} className={`glass pointer-events-auto flex h-[44px] items-center gap-[2px] rounded-[22px] px-[4px] pointer-coarse:h-[52px] pointer-coarse:rounded-[26px] ${className}`}>
      {children}
    </div>
  );
}

/** A hairline between runs inside one capsule. */
export function ToolbarSeparator() {
  return <span aria-hidden className="mx-[4px] h-[20px] w-px shrink-0 bg-separator" />;
}

type ToolbarButtonProps = {
  icon: IconName;
  label: string;
  shortcut?: string;
  /** Show a word beside the mark when there is room. */
  showLabel?: boolean;
  tone?: 'neutral' | 'accent' | 'soft';
  /** A tool in hand, or a toggle that is on: a raised pill and the filled mark. */
  selected?: boolean;
  /** A small chevron under the mark: this opens a menu of choices. */
  more?: boolean;
  /** Mirror the mark, for the one direction of a pair Streamline draws. */
  mirror?: boolean;
  iconSize?: number;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>;

export const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(function ToolbarButton(
  { icon, label, shortcut, selected, showLabel = false, tone = 'neutral', more = false, mirror = false, iconSize = ICON_SIZE.control, className = '', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      // Only a toggle says whether it is pressed: a tool, or a panel that is out. A plain
      // action (All Files, Undo) is not announced as a switch that happens to be off.
      aria-pressed={rest['aria-haspopup'] || selected === undefined ? undefined : selected}
      {...tip(label, shortcut)}
      {...rest}
      className={`relative flex shrink-0 items-center justify-center gap-[6px] rounded-[18px] transition-[background-color,color] duration-150 disabled:opacity-35 pointer-coarse:rounded-[22px] ${showLabel ? 'h-[36px] px-[10px] pointer-coarse:h-[44px]' : 'size-[36px] pointer-coarse:size-[44px]'} ${
        selected ? (tone === 'accent' ? 'bg-accent text-on-accent' : tone === 'soft' ? 'bg-accent-soft text-accent-text' : 'bg-fill-selected text-label') : 'text-label hover:bg-fill-2 active:bg-fill-pressed'
      } ${className}`}
    >
      <span className={`inline-flex items-center justify-center leading-none ${mirror ? '-scale-x-100' : ''}`}>
        <Icon name={icon} size={iconSize} solid={!!selected} />
      </span>
      {showLabel ? <span className="text-[13px] font-medium">{label}</span> : null}
      {more ? <span aria-hidden className="absolute bottom-[5px] right-[6px] size-0 border-l-[3px] border-t-[3px] border-l-transparent border-t-current opacity-60" /> : null}
    </button>
  );
});

// ── buttons ──────────────────────────────────────────────────────────────────

type ButtonProps = {
  variant?: 'plain' | 'prominent' | 'borderless' | 'destructive';
  size?: 'small' | 'regular' | 'large';
  icon?: IconName;
  children?: ReactNode;
  full?: boolean;
} & ButtonHTMLAttributes<HTMLButtonElement>;

/** A push button: a capsule, as 26 and 27 draw them. One prominent button per view. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'plain', size = 'regular', icon, children, full = false, className = '', ...rest },
  ref,
) {
  const height = size === 'small' ? 'h-[24px] px-[10px] text-[12px]' : size === 'large' ? 'h-[34px] px-[16px] text-[13px]' : 'h-[28px] px-[12px] text-[13px]';
  const look =
    variant === 'prominent'
      ? 'bg-accent text-on-accent hover:bg-accent-hover font-medium'
      : variant === 'borderless'
        ? 'text-accent-text hover:bg-fill-2'
        : variant === 'destructive'
          ? 'bg-fill-1 text-danger hover:bg-fill-selected'
          : 'bg-fill-1 text-label hover:bg-fill-selected active:bg-fill-pressed';
  return (
    <button
      ref={ref}
      type="button"
      {...rest}
      className={`inline-flex shrink-0 items-center justify-center gap-[6px] whitespace-nowrap rounded-full transition-colors duration-150 disabled:opacity-40 ${height} ${look} ${full ? 'w-full' : ''} ${className}`}
    >
      {icon ? <Icon name={icon} size={size === 'small' ? 14 : 16} /> : null}
      {children}
    </button>
  );
});

/** An empty collection needs one clear next step, rather than a second heading. */
export const EmptyAction = forwardRef<HTMLButtonElement, ButtonProps>(function EmptyAction({ className = '', children, ...props }, ref) {
  return <Button ref={ref} full size="large" variant="borderless" {...props} className={`empty-action ${className}`}><span className="empty-action-label">{children}</span></Button>;
});

/**
 * Something is on its way: a small ring that turns, in the colour of the text beside it.
 * It goes BESIDE a control's label, never in place of it.
 */
export function Spinner({ size = 12, className = '' }: { size?: number; className?: string }) {
  return (
    <span
      aria-hidden
      className={`inline-block shrink-0 animate-spin rounded-full border-[1.5px] border-current border-r-transparent ${className}`}
      style={{ width: size, height: size }}
    />
  );
}

/** A small borderless mark inside a panel: add, remove, show, hide. */
export const IconButton = forwardRef<
  HTMLButtonElement,
  { icon: IconName; label: string; shortcut?: string; selected?: boolean; size?: number; iconSize?: number; mirror?: boolean; tone?: 'default' | 'muted' | 'danger' } & Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    'children'
  >
>(function IconButton({ icon, label, shortcut, selected = false, size = 24, iconSize = ICON_SIZE.small, mirror = false, tone = 'muted', className = '', ...rest }, ref) {
  const colour = selected ? 'text-label' : tone === 'danger' ? 'text-danger' : tone === 'default' ? 'text-label' : 'text-label-2 hover:text-label';
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      aria-pressed={selected || undefined}
      {...tip(label, shortcut)}
      {...rest}
      className={`grid shrink-0 place-items-center rounded-[7px] transition-colors duration-150 hover:bg-fill-2 active:bg-fill-pressed disabled:opacity-35 ${
        selected ? 'bg-fill-selected' : ''
      } ${colour} ${className}`}
      style={{ width: size, height: size }}
    >
      <span className={`flex items-center justify-center ${mirror ? '-scale-x-100' : ''}`}>
        <Icon name={icon} size={iconSize} solid={selected} />
      </span>
    </button>
  );
});

// ── segmented control ────────────────────────────────────────────────────────

export interface Segment<T extends string> {
  readonly value: T;
  readonly label?: string;
  readonly icon?: IconName;
  /** The help tag, for a segment that is only a mark. */
  readonly tip?: string;
  readonly disabled?: boolean;
  /** The tool guide a long hover shows (editor/guides), for a segment that is a tool's mode. */
  readonly guide?: string;
  /** A small accent dot after the label: something is set behind this segment. */
  readonly badge?: boolean;
}

/**
 * A segmented control: a capsule track with a thumb that slides to the chosen segment.
 *
 * Text or marks, never both in one control. Every segment is the same width, so the
 * thumb's travel is a single transform — which is also what makes it slide rather than
 * jump. With `value` null nothing is chosen and there is no thumb: a control of actions
 * rather than of states.
 */
export function SegmentedControl<T extends string>({
  segments,
  value,
  onChange,
  size = 'regular',
  label,
  className = '',
}: {
  segments: readonly Segment<T>[];
  value: T | null;
  onChange: (value: T) => void;
  size?: 'small' | 'regular';
  label: string;
  className?: string;
}) {
  const index = value === null ? -1 : segments.findIndex((segment) => segment.value === value);
  const height = size === 'small' ? 24 : 28;
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={`relative flex rounded-full bg-fill-1 p-[2px] ${className}`}
      style={{ height }}
      onKeyDown={(event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const by = event.key === 'ArrowRight' ? 1 : -1;
        for (let i = 1; i <= segments.length; i++) {
          const next = segments[(index + by * i + segments.length * 2) % segments.length]!;
          if (!next.disabled) {
            onChange(next.value);
            break;
          }
        }
      }}
    >
      {index >= 0 ? (
        <span
          aria-hidden
          className="absolute bottom-[2px] top-[2px] rounded-full transition-transform duration-200 ease-[cubic-bezier(0.2,0.9,0.3,1)]"
          style={{
            left: 2,
            width: `calc((100% - 4px) / ${segments.length})`,
            transform: `translateX(${index * 100}%)`,
            background: 'var(--seg-thumb)',
            boxShadow: 'var(--seg-thumb-shadow)',
          }}
        />
      ) : null}
      {segments.map((segment, at) => {
        const chosen = at === index;
        return (
          <button
            key={segment.value}
            type="button"
            role="radio"
            aria-checked={chosen}
            aria-label={segment.label ?? segment.tip}
            tabIndex={chosen || (index < 0 && at === 0) ? 0 : -1}
            disabled={segment.disabled}
            {...(segment.tip ? tip(segment.tip) : {})}
            data-guide={segment.guide}
            onClick={() => onChange(segment.value)}
            className={`relative z-[1] flex min-w-0 flex-1 items-center justify-center gap-[5px] rounded-full transition-colors duration-150 disabled:opacity-35 ${
              size === 'small' ? 'px-[3px] text-[11px]' : 'px-[6px] text-[12px]'
            } ${chosen ? 'font-medium text-label' : 'text-label-2 hover:text-label'}`}
          >
            {segment.icon ? <Icon name={segment.icon} size={size === 'small' ? 14 : 16} solid={chosen} /> : null}
            {segment.label && !segment.icon ? <span className="truncate">{segment.label}</span> : null}
            {segment.badge ? <span aria-hidden className="size-[5px] shrink-0 rounded-full bg-accent" /> : null}
          </button>
        );
      })}
    </div>
  );
}

// ── slider ───────────────────────────────────────────────────────────────────

export function Slider({
  value,
  min,
  max,
  step = 1,
  onChange,
  label,
  disabled,
  className = '',
  track,
  resetTo,
}: {
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
  label: string;
  disabled?: boolean;
  className?: string;
  /** A painted track (a colour ramp), in place of the accent fill. */
  track?: string;
  /** A double-click puts it back to this, as in Lightroom and Photoshop (Tarek, 15 Oct). */
  resetTo?: number;
}) {
  const span = max - min || 1;
  const fill = `${Math.max(0, Math.min(100, ((value - min) / span) * 100))}%`;
  return (
    <input
      type="range"
      aria-label={label}
      min={min}
      max={max}
      step={step}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(Number(event.target.value))}
      // One drag, one undo step (QA 2026-09-24, inspector-numbers-F10).
      onPointerDown={() => beginHold()}
      // Let go of the keyboard when the hand lets go of the thumb: a slider that kept
      // focus after a drag kept the arrow keys too, and they changed it instead of
      // nudging the shape.
      onPointerUp={(event) => event.currentTarget.blur()}
      onDoubleClick={resetTo === undefined || disabled ? undefined : () => onChange(resetTo)}
      className={`ui-slider ${track ? 'ui-slider--painted' : ''} ${className}`}
      style={{ '--fill': fill, ...(track ? { '--track': track } : {}) } as CSSProperties}
    />
  );
}

// ── fields ───────────────────────────────────────────────────────────────────

/**
 * A number, typed.
 *
 * Kept as text while it is being typed, so a half-written "1." is not read as a one and
 * rounded away under the hand; said on Return or when the field is left. The arrows step
 * it (shift for ten), and the small label at the front is a handle: drag it sideways to
 * scrub the value, the way every design tool since Photoshop has let you.
 */
export function NumberField({
  value,
  onChange,
  prefix,
  prefixWidth,
  suffix,
  step = 1,
  min,
  max,
  decimals = 1,
  label,
  disabled,
  className = '',
  format,
  parse,
}: {
  value: number;
  onChange: (value: number) => void;
  /** A letter or mark inside the field, before the number: X, Y, W, ° … */
  prefix?: ReactNode;
  /** How wide that label is, when it is a word ("Turn") rather than a letter. */
  prefixWidth?: number;
  suffix?: string;
  step?: number;
  min?: number;
  max?: number;
  decimals?: number;
  label: string;
  disabled?: boolean;
  className?: string;
  /** How the value reads, when it is not simply the number (a length in the document's unit). */
  format?: (value: number) => string;
  /** And how typed text turns back into a value. Null refuses it. */
  parse?: (text: string) => number | null;
}) {
  const shown = format ? format(value) : String(roundTo(value, decimals));
  const [typed, setTyped] = useState<string | null>(null);
  /** Escape was pressed: the blur that follows must not apply what was typed. */
  const cancelled = useRef(false);
  const clamp = (n: number) => Math.max(min ?? -Infinity, Math.min(max ?? Infinity, n));
  const commit = () => {
    if (cancelled.current) {
      cancelled.current = false;
      setTyped(null);
      return;
    }
    if (typed !== null) {
      // Empty or not a number keeps the old value; a comma is a decimal point
      // (QA 2026-09-24, inspector-numbers-F12).
      const parsed = parse ? parse(typed) : readNumber(typed);
      // To the field's own precision: a whole-number field given 3.5 (Polygon sides) wrote
      // 3.5, and every shape the tool made after it was refused (QA 2026-09-24, shapes-F6).
      if (parsed !== null && Number.isFinite(parsed)) onChange(clamp(parse ? parsed : roundTo(parsed, decimals)));
    }
    setTyped(null);
  };
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const { scrubbing, handlers: scrubHandlers } = useNumberScrub({ value, step, min, max, decimals, disabled, onChange: next => { setTyped(null); onChange(next); } }, inputRef);

  return (
    <div
      data-scrubbing={scrubbing || undefined}
      className={`number-field group flex h-[26px] min-w-0 items-center rounded-[8px] bg-fill-1 field-focus ${disabled ? 'opacity-40' : ''} ${className}`}
    >
      {prefix !== undefined && <label
        htmlFor={id}
        aria-label={`Drag to adjust ${label}`}
        title="Drag to adjust · Shift for larger steps · Option for fine adjustment"
        className={`number-scrub grid h-full shrink-0 cursor-ew-resize touch-none select-none place-items-center text-[11px] text-label-2 ${prefixWidth ? 'whitespace-nowrap px-[6px]' : 'w-[26px]'}`}
        style={prefixWidth ? { width: prefixWidth } : undefined}
        {...scrubHandlers}
        onClick={event => event.preventDefault()}
      >{prefix}</label>}
      <input
        ref={inputRef}
        {...scrubHandlers}
        id={id}
        aria-label={label}
        value={typed ?? shown}
        disabled={disabled}
        inputMode="decimal"
        onChange={(event) => setTyped(event.target.value)}
        onFocus={(event) => event.currentTarget.select()}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
          else if (event.key === 'Escape') {
            // The blur fires commit at once, before React forgets the typed text — so it
            // applied what Escape meant to throw away (QA 2026-09-24, inspector-numbers-F2).
            cancelled.current = true;
            setTyped(null);
            event.currentTarget.blur();
          } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
            const by = (event.key === 'ArrowUp' ? step : -step) * (event.shiftKey ? 10 : 1);
            // From the number just typed, when there is one: the arrow threw it away and
            // stepped from the old value (QA 2026-09-24, inspector-numbers-F15).
            const pending = typed === null ? null : parse ? parse(typed) : readNumber(typed);
            const from = pending !== null && Number.isFinite(pending) ? clamp(pending) : value;
            setTyped(null);
            onChange(clamp(roundTo(from + by, decimals)));
          }
          event.stopPropagation();
        }}
        className="number-input tnum h-full w-0 min-w-0 flex-1 bg-transparent px-[6px] text-[12px] text-label outline-none"
      />
      {suffix ? <span className="shrink-0 pr-[7px] text-[11px] text-label-3">{suffix}</span> : null}
    </div>
  );
}

export function TextField({
  value,
  onChange,
  placeholder,
  label,
  icon,
  autoFocus,
  className = '',
  onEnter,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label: string;
  icon?: IconName;
  autoFocus?: boolean;
  className?: string;
  onEnter?: () => void;
}) {
  return (
    <div className={`flex h-[28px] items-center gap-[6px] rounded-[9px] bg-fill-1 px-[8px] field-focus ${className}`}>
      {icon ? (
        <span className="text-label-3">
          <Icon name={icon} size={14} />
        </span>
      ) : null}
      <input
        aria-label={label}
        value={value}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') onEnter?.();
          event.stopPropagation();
        }}
        className="w-0 min-w-0 flex-1 bg-transparent text-[13px] text-label outline-none placeholder:text-label-3"
      />
    </div>
  );
}

// ── switch ───────────────────────────────────────────────────────────────────

export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (on: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative h-[20px] w-[34px] shrink-0 rounded-full transition-colors duration-200 disabled:opacity-40 ${checked ? 'bg-accent' : 'bg-fill-selected'}`}
    >
      <span
        aria-hidden
        className="absolute top-[2px] size-[16px] rounded-full transition-transform duration-200 ease-[cubic-bezier(0.2,0.9,0.3,1.1)]"
        style={{ left: 2, background: 'var(--knob)', boxShadow: 'var(--knob-shadow)', transform: checked ? 'translateX(14px)' : 'none' }}
      />
    </button>
  );
}

// ── pop-up button ────────────────────────────────────────────────────────────

/**
 * A pop-up button: the choice it holds, and the up-and-down chevrons that say it is one.
 *
 * The list is OUR menu (`ui/menu`), not the browser's select: the same glass, rows,
 * checkmark, arrows and type-ahead as every other menu in the editor. The browser's own
 * list was the system's grey one, which belonged to nothing else on the screen (Tarek,
 * 2026-09-30: "why are we using the browser default dropdowns and not ours?").
 */
export function PopUpButton<T extends string>({
  value,
  options,
  onChange,
  label,
  className = '',
  disabled,
}: {
  value: T;
  options: readonly (readonly [T, string])[];
  onChange: (value: T) => void;
  label: string;
  className?: string;
  disabled?: boolean;
}) {
  const [anchor, setAnchor] = useAnchor<HTMLButtonElement>();
  const [open, setOpen] = useState(false);
  const name = options.find(([option]) => option === value)?.[1] ?? '';
  return (
    <>
      {/* The box is the control's shape in its row — the same one it had as a select — and the button fills it. */}
      <div className={`field-focus relative flex h-[26px] min-w-0 items-center rounded-[8px] bg-fill-1 transition-colors hover:bg-fill-selected ${open ? 'bg-fill-selected' : ''} ${disabled ? 'opacity-40' : ''} ${className}`}>
        <button
          ref={setAnchor}
          type="button"
          aria-label={name ? `${label}: ${name}` : label}
          aria-haspopup="menu"
          aria-expanded={open}
          disabled={disabled}
          onClick={() => setOpen((was) => !was)}
          onKeyDown={(event) => {
            // As a pop-up button does on a Mac: the arrows open it too.
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              setOpen(true);
            }
          }}
          className="h-full w-full min-w-0 cursor-default truncate rounded-[inherit] bg-transparent pl-[9px] pr-[24px] text-left text-[12px] text-label outline-none"
        >
          {name}
        </button>
        <span aria-hidden className="pointer-events-none absolute right-[9px] top-1/2 flex -translate-y-1/2 text-label-2 leading-none">
          <Icon name="chevron-up-down" size={ICON_SIZE.indicator} />
        </span>
      </div>
      <Menu
        open={open}
        anchor={anchor}
        onClose={() => {
          setOpen(false);
          // Back to the button the menu came from, unless the press that closed it went somewhere else.
          if (anchor && (document.activeElement === document.body || document.activeElement?.closest('[role="menu"]'))) anchor.focus({ preventScroll: true });
        }}
        label={label}
        // Never narrower than the button it hangs from.
        width={Math.max(140, anchor?.offsetWidth ?? 0)}
        current={value}
        items={options.map(([option, title]) => ({ id: option, label: title, checked: option === value, onSelect: () => option !== value && onChange(option) }))}
      />
    </>
  );
}

// ── colour well ──────────────────────────────────────────────────────────────

/** A swatch that opens the colour picker. Alpha shows over a checkerboard; none, as a slash. */
export const ColorWell = forwardRef<HTMLButtonElement, { colour: string | null; alpha?: number; label: string; onClick: () => void; active?: boolean; size?: 'small' | 'regular' }>(
  function ColorWell({ colour, alpha = 1, label, onClick, active = false, size = 'regular' }, ref) {
    const box = size === 'small' ? 'h-[20px] w-[20px] rounded-[6px]' : 'h-[24px] w-[34px] rounded-[7px]';
    return (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        {...tip(label)}
        onClick={onClick}
        className={`relative shrink-0 overflow-hidden ${box} ${active ? 'ring-[2.5px] ring-accent ring-offset-[1.5px] ring-offset-[var(--glass-thick)]' : ''}`}
        style={{
          boxShadow: 'inset 0 0 0 0.5px rgb(0 0 0 / 0.2), 0 0 0 0.5px var(--glass-border)',
          background: colour ? 'repeating-conic-gradient(#c8c8c8 0% 25%, #ffffff 0% 50%) 50% / 8px 8px' : 'var(--fill-1)',
        }}
      >
        {colour ? <span aria-hidden className="absolute inset-0" style={{ background: colour, opacity: alpha }} /> : <NoneSlash />}
      </button>
    );
  },
);

/** "None", drawn the way every drawing program has drawn it since the first one. */
export function NoneSlash() {
  return (
    <span
      aria-hidden
      className="absolute inset-0"
      style={{
        background:
          'linear-gradient(to top right, transparent calc(50% - 0.75px), var(--danger) calc(50% - 0.75px), var(--danger) calc(50% + 0.75px), transparent calc(50% + 0.75px))',
      }}
    />
  );
}

// ── layout ───────────────────────────────────────────────────────────────────

/**
 * A section of the side panel: a title, what belongs under it, and a way to fold it.
 *
 * Folding is remembered per title, so the sections somebody never uses stay out of their
 * way from one selection to the next.
 */
export function Section({
  title,
  children,
  accessory,
  collapsible = true,
  id,
  revealKey,
  emptyAction,
  defaultOpen = true,
}: {
  title: string;
  children: ReactNode;
  accessory?: ReactNode;
  collapsible?: boolean;
  id?: string;
  /** Reveal populated sections on entry and whenever their item count changes. */
  revealKey?: number;
  /** Replace the heading and content with the next step while the collection is empty. */
  emptyAction?: ReactNode;
  defaultOpen?: boolean;
}) {
  const key = `artwork.section.${id ?? title}`;
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(key);
      return saved === 'open' || (saved !== 'closed' && defaultOpen);
    } catch {
      return defaultOpen;
    }
  });
  const section = useRef<HTMLElement>(null);
  const previousReveal = useRef(revealKey);
  useEffect(() => {
    const added = revealKey !== undefined && revealKey > (previousReveal.current ?? 0);
    previousReveal.current = revealKey;
    if (revealKey) setOpen(true);
    // Run after the inspector restores its scroll position. Value changes never
    // enter this path; only adding a row brings its new controls into view.
    if (added) {
      const frame = requestAnimationFrame(() => {
        section.current?.lastElementChild?.scrollIntoView({ block: 'nearest' });
        // Keep the inspector's restoration cache in sync before another document
        // render can restore the previous offset. Native scroll events are deferred.
        section.current?.closest('.ui-scroll')?.dispatchEvent(new Event('scroll'));
      });
      return () => cancelAnimationFrame(frame);
    }
  }, [revealKey]);
  useEffect(() => {
    try {
      if (open && defaultOpen) localStorage.removeItem(key);
      else localStorage.setItem(key, open ? 'open' : 'closed');
    } catch {
      // Private windows keep it for the visit.
    }
  }, [key, open, defaultOpen]);
  return (
    <section ref={section} data-property-section={id ?? title} aria-label={emptyAction ? title : undefined} data-empty={emptyAction ? '' : undefined} className="property-section border-t border-separator px-[14px] pb-[12px] pt-[10px] first:border-t-0">
      {/* Empty, a section is still a heading — its name, quieter, and its Add at the end —
          so Fill, Stroke and Effects read as one list whatever they hold (Tarek, 2 Nov: "look
          at the figma one"). The action keeps its words for a screen reader. */}
      {emptyAction ? <div className="property-section-heading flex h-[22px] items-center gap-[6px]">
        <span className="property-section-title min-w-0 flex-1 truncate text-[13px] font-semibold">{title}</span>
        {emptyAction}
      </div> : <>
      <div className="property-section-heading flex h-[22px] items-center gap-[6px]">
        {collapsible ? (
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((was) => !was)}
            className="group flex min-w-0 flex-1 items-center gap-[4px] text-left"
          >
            <span className="property-section-title truncate text-[13px] font-semibold text-label">{title}</span>
            <span className={`property-section-chevron text-label-3 opacity-0 transition-[opacity,transform] duration-150 group-hover:opacity-100 ${open ? 'rotate-90' : ''}`}>
              <Icon name="chevron-right" size={11} />
            </span>
          </button>
        ) : (
          <span className="property-section-title min-w-0 flex-1 truncate text-[13px] font-semibold text-label">{title}</span>
        )}
        {accessory}
      </div>
      {(!collapsible || open) && Children.toArray(children).length > 0 ? <div className="mt-[8px] grid grid-cols-[minmax(0,1fr)] gap-[8px]">{children}</div> : null}
      </>}
    </section>
  );
}

/** A labelled row: the name on the leading side, the control filling the rest. */
export function Row({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <div className="flex min-h-[26px] items-center gap-[10px]" {...(hint ? tip(hint) : {})}>
      <span className="w-[64px] shrink-0 truncate text-[12px] text-label-2">{label}</span>
      <div className="flex min-w-0 flex-1 items-center gap-[6px]">{children}</div>
    </div>
  );
}

/** Help text under a control: short, secondary, never a paragraph. */
export function Hint({ children }: { children: ReactNode }) {
  return <p className="text-[11px] leading-[14px] text-label-2">{children}</p>;
}

/** A person, as a circle with their initial, in the colour their cursor wears. */
export function Avatar({ name, colour, size = 22 }: { name: string; colour: string; size?: number }) {
  return (
    <span
      aria-label={name}
      {...tip(name)}
      className="grid shrink-0 place-items-center rounded-full text-[10px] font-semibold text-white"
      style={{ width: size, height: size, background: colour, boxShadow: '0 0 0 1.5px var(--glass-thick)' }}
    >
      {name.slice(0, 1).toUpperCase()}
    </span>
  );
}

const roundTo = (value: number, decimals: number) => {
  const f = 10 ** decimals;
  return Math.round(value * f) / f;
};
